import { QuizQuestion } from '../split-screen-player.types';
import { normalizeAnswer } from '@shared/utils/normalize-answer';
import { roundPercentage } from '@shared/utils/quiz-pass.utils';
import {
  isMcGridColumnAccepted,
  isMcGridRowCorrect as isMcGridRowCorrectCell
} from '../../quiz-builder/utils/mc-grid-answer.utils';

// ─── Types ────────────────────────────────────────────────────────

export interface QuizFeedbackItem {
  questionIndex: number;
  isCorrect: boolean;
  status: 'correct' | 'incorrect' | 'partial' | 'manual' | 'recorded';
  score: number;
  maxScore: number;
  // Raw values drive template equality checks (e.g. border-warning === displayIdx).
  correctAnswer: any;
  userAnswer: any;
  // Display variants carry resolved text (option text, "True"/"False", joined lists)
  // for rendering the ✗ / ✓ feedback lines without leaking raw indices (KAN-178).
  correctAnswerDisplay: any;
  userAnswerDisplay: any;
  points: number;
  requiresManualGrading: boolean;
  feedback?: { general?: string; correct?: string; incorrect?: string } | null;
}

export interface GradeQuizResult {
  feedback: QuizFeedbackItem[];
  autoEarnedPoints: number;
  autoGradablePoints: number;
  manualGradingPoints: number;
  totalPoints: number;
  earnedPoints: number;
  /**
   * Integer-rounded percentage for DISPLAY only. KAN-239: never use this for
   * pass/fail — use {@link rawPercentageScore} with {@link isQuizPassing}.
   */
  percentageScore: number;
  /**
   * Percentage rounded HALF_UP to 1 decimal place, matching the backend
   * canonical boundary contract (computeQuizResult). Use this — never the
   * integer `percentageScore` — when evaluating against the passing threshold.
   */
  rawPercentageScore: number;
}

export interface PartialCreditResult {
  score: number;
  maxScore: number;
  correctCount: number;
  incorrectCount: number;
  totalCorrect: number;
}

// ─── Pure grading functions ───────────────────────────────────────

export function toBooleanValue(value: any): boolean {
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    if (lower === 'false' || lower === '0' || lower === '') {
      return false;
    }
    if (lower === 'true' || lower === '1') {
      return true;
    }
  }
  return Boolean(value);
}

export function getWordCount(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

export function getQuestionPoints(question: QuizQuestion): number {
  // KAN-333: matching shares the grids' single question-level budget model
  // (`totalPoints ?? points ?? 1`) — never a sum of per-pair points.
  if (question.type === 'multiple-choice-grid' || question.type === 'checkbox-grid' || question.type === 'matching') {
    // KAN-241: a grid is worth a single question-level point budget. Mirror
    // the backend engine's denominator (`totalPoints ?? points ?? 1`) — do
    // NOT sum per-row points. Summing was the >=N inflation trap: a perfect
    // floored grid would contribute the row count to the numerator while the
    // quiz denominator counts only the single budget (300% inflation).
    if (question.totalPoints !== undefined && question.totalPoints !== null) {
      return question.totalPoints;
    }
    if (question.points !== undefined && question.points !== null) {
      return question.points;
    }
    return 1;
  }

  if (question.type === 'select-all') {
    // Align label with backend engine, which uses `totalPoints ?? points ?? 1`
    // (see backend quiz-grading.ts gradeQuestion). Prefer totalPoints (V2
    // builder writes this from option sums); fall back to question.points
    // (AI-generated questions only have points); finally collapse to the single
    // question budget of 1.
    //
    // KAN-241 code-review H1: the old `sum(option.points || 1)` fallback was a
    // re-inflation vector — with neither totalPoints nor points, it summed to
    // the OPTION COUNT, re-introducing the >=N inflation the single-points model
    // exists to kill. A select-all with neither field is worth exactly 1.
    if (question.totalPoints !== undefined && question.totalPoints !== null) {
      return question.totalPoints;
    }
    if (question.points !== undefined && question.points !== null) {
      return question.points;
    }
    return 1;
  }

  // Followup KAN-225: mirror backend's `points ?? 1` (gradeQuestion) so a
  // legitimate `points: 0` acknowledgment question (KAN-223) is preserved
  // as 0 instead of inflating to 1 via the legacy `||` short-circuit.
  return question.points ?? 1;
}

/**
 * Compute the points awarded to a single row of a grid-style question.
 * Used for the per-row "+N" feedback chip on checkbox-grid and
 * multiple-choice-grid (KAN-193).
 *
 * KAN-241: a grid is worth a single question-level point budget and each
 * correct row earns an EQUAL share of it (`questionPoints / rowCount`). This
 * mirrors the backend's proportional `(correctWeight / totalWeight) *
 * questionPoints` with unweighted rows. Rows no longer carry per-row points,
 * so the old `(row.points || 1)` resolution is gone — it inflated a perfect
 * floored grid to the row count.
 *
 * Rounds to 1 decimal place for display. Non-grid questions return 0.
 */
export function getPerRowPoints(question: QuizQuestion, rowIndex?: number, budgetOverride?: number): number {
  // KAN-348 QA: attempt snapshots freeze the EFFECTIVE budget on
  // question.points; callers rendering a snapshot pass it via budgetOverride
  // so per-row shares match what grading awards. Default: authored KAN-241
  // budget (getQuestionPoints).
  // KAN-333: matching shares the equal-share model; each correct pair earns
  // questionPoints / leftItemCount.
  if (question.type === 'matching') {
    const left = question.matchLeft;
    if (!left || !Array.isArray(left) || left.length === 0) {
      return 0;
    }
    const budget = budgetOverride ?? getQuestionPoints(question);
    return Math.round((budget / left.length) * 10) / 10;
  }

  if (question.type !== 'multiple-choice-grid' && question.type !== 'checkbox-grid') {
    return 0;
  }

  const rows = question.gridRows;
  if (!rows || !Array.isArray(rows) || rows.length === 0) {
    return 0;
  }

  // KAN-241: every row earns the same equal share of the question budget,
  // regardless of rowIndex.
  const questionPoints = budgetOverride ?? getQuestionPoints(question);
  const share = questionPoints / rows.length;
  return Math.round(share * 10) / 10;
}

export function getTotalQuizPoints(questions: QuizQuestion[]): number {
  return questions.reduce((total, question) => total + getQuestionPoints(question), 0);
}

export function checkForManualGradingQuestions(questions: QuizQuestion[]): { hasManual: boolean; pendingCount: number } {
  const paragraphQuestions = questions.filter(q => q.type === 'paragraph');
  return {
    hasManual: paragraphQuestions.length > 0,
    pendingCount: paragraphQuestions.length
  };
}

export function previewGradeSelectAllWithPartialCredit(question: QuizQuestion, userAnswer: number[]): PartialCreditResult {
  const points = getQuestionPoints(question);
  const maxScore = points;

  const normalizedAnswer = Array.isArray(userAnswer) ? [...new Set(userAnswer)] : [];

  const scoringMode = (question.scoringMode || 'all-or-nothing').toLowerCase().trim();

  if (scoringMode !== 'all-or-nothing' && scoringMode !== 'partial') {
    return { score: 0, maxScore, correctCount: 0, incorrectCount: 0, totalCorrect: 0 };
  }

  let correctIndices: number[] = [];
  if (Array.isArray(question.answer)) {
    const firstElement = question.answer[0];
    const isBooleanArray = typeof firstElement === 'boolean';

    if (isBooleanArray) {
      correctIndices = question.answer
        .map((isCorrect: any, index: number) => isCorrect ? index : null)
        .filter((index: any): index is number => index !== null);
    } else {
      correctIndices = question.answer as number[];
    }
  }

  const totalCorrect = correctIndices.length;

  if (totalCorrect === 0) {
    return { score: 0, maxScore, correctCount: 0, incorrectCount: 0, totalCorrect: 0 };
  }

  const correctCount = normalizedAnswer.filter(index => correctIndices.includes(index)).length;
  const incorrectCount = normalizedAnswer.filter(index => !correctIndices.includes(index)).length;

  const rawScore = ((correctCount - incorrectCount) / totalCorrect) * points;
  const score = Math.min(points, Math.max(0, rawScore));

  return { score, maxScore, correctCount, incorrectCount, totalCorrect };
}

/**
 * Resolve an arbitrary answer value (correct OR user-submitted) to human-readable
 * display text for quiz feedback. Used symmetrically for `correctAnswerDisplay`
 * and `userAnswerDisplay` so both the ✓ and ✗ feedback lines render option text
 * instead of raw indices (KAN-178).
 *
 * For multiple-choice / select-all the stored value is an index (or index array,
 * or boolean-array encoding) into `question.options`; for true-false it's a
 * boolean; for short-answer it's a string (falls back to acceptedAnswers when
 * resolving the correct side).
 */
/**
 * KAN-229: resolve display text for a single option, falling back to the image
 * alt text and finally "Image option N" for image-only options (no text).
 */
function resolveOptionDisplay(option: any, index: number): string {
  if (option == null) return String(index);
  if (typeof option === 'string') return option;
  const text = option.text;
  if (typeof text === 'string' && text.trim() !== '') return text;
  const alt = option.image?.alt;
  if (typeof alt === 'string' && alt.trim() !== '') return alt;
  if (option.image?.url) return `Image option ${index + 1}`;
  return option.text ?? option;
}

export function resolveAnswerForDisplay(question: QuizQuestion, answer: any): any {
  const type = question.type || 'multiple-choice';

  switch (type) {
    case 'multiple-choice': {
      const options = (question as any).options;
      if (Array.isArray(options) && typeof answer === 'number' && options[answer]) {
        return resolveOptionDisplay(options[answer], answer);
      }
      return answer;
    }

    case 'true-false':
      return (answer === true || answer === 'true' || answer === 1) ? 'True' : 'False';

    case 'short-answer':
      // For the CORRECT answer, prefer acceptedAnswers join; for the USER answer,
      // pass through as-is so the template can handle quoting.
      if (answer === question.answer
          && question.acceptedAnswers
          && Array.isArray(question.acceptedAnswers)
          && question.acceptedAnswers.length > 0) {
        // KAN-247: join with " / " (not " or ") so accepted answers that
        // themselves contain the word "or" stay visually distinguishable in
        // the feedback banner.
        return question.acceptedAnswers.join(' / ');
      }
      return answer;

    case 'select-all': {
      const options = (question as any).options;
      let indices: number[] = [];

      if (Array.isArray(answer)) {
        const firstElement = answer[0];
        if (typeof firstElement === 'boolean') {
          indices = answer
            .map((isCorrect: any, index: number) => isCorrect ? index : -1)
            .filter((index: number) => index !== -1);
        } else {
          indices = answer as number[];
        }
      }

      if (Array.isArray(options) && indices.length > 0) {
        const texts = indices
          .map(i => resolveOptionDisplay(options[i], i))
          .filter(v => v !== undefined && v !== null && v !== '');
        if (texts.length > 0) {
          return texts.join(', ');
        }
      }

      return indices.join(', ');
    }

    case 'matching': {
      // KAN-333: render "Left -> Right; ..." for either the correct answer key
      // or the learner's picks (both are number[] of matchRight indices).
      const left = (question as any).matchLeft;
      const right = (question as any).matchRight;
      if (!Array.isArray(answer) || !Array.isArray(left) || !Array.isArray(right)) {
        return answer;
      }
      return left
        .map((leftItem: any, i: number) => {
          const leftLabel = leftItem?.label ?? `Item ${i + 1}`;
          const picked = answer[i];
          const rightLabel = (typeof picked === 'number' && right[picked]) ? right[picked].label : '—';
          return `${leftLabel} → ${rightLabel}`;
        })
        .join('; ');
    }

    default:
      return answer;
  }
}

// ─── Main grading function ────────────────────────────────────────

export function gradeQuizForPreview(questions: QuizQuestion[], userAnswers: any[]): GradeQuizResult {
  let totalPoints = 0;
  let earnedPoints = 0;
  let manualGradingPoints = 0;
  let autoGradablePoints = 0;
  let autoEarnedPoints = 0;
  const feedback: QuizFeedbackItem[] = [];

  questions.forEach((question, index) => {
    const userAnswer = userAnswers[index];
    let points = question.type === 'information-collection' ? 0 : getQuestionPoints(question);
    totalPoints += points;

    let isCorrect = false;

    switch (question.type) {
      case 'multiple-choice':
        isCorrect = userAnswer === question.answer;
        break;
      case 'select-all':
        const scoringMode = (question.scoringMode || 'all-or-nothing').toLowerCase().trim();

        if (scoringMode === 'partial' && Array.isArray(userAnswer)) {
          const partialResult = previewGradeSelectAllWithPartialCredit(question, userAnswer);
          (question as any)._partialData = partialResult;
          isCorrect = partialResult.score === partialResult.maxScore;
        } else if (Array.isArray(userAnswer) && Array.isArray(question.answer)) {
          const firstElement = question.answer[0];
          const isBooleanArray = typeof firstElement === 'boolean';

          let correctIndices: number[];
          if (isBooleanArray) {
            correctIndices = question.answer
              .map((isCorrect: any, idx: number) => isCorrect ? idx : null)
              .filter((idx: any): idx is number => idx !== null);
          } else {
            correctIndices = question.answer;
          }

          isCorrect = userAnswer.length === correctIndices.length &&
                     userAnswer.every(ans => correctIndices.includes(ans));
        }
        break;
      case 'short-answer':
        if (question.acceptedAnswers && Array.isArray(question.acceptedAnswers) && question.acceptedAnswers.length > 0) {
          const normalizedUserInput = normalizeAnswer(userAnswer || '', question);

          isCorrect = question.acceptedAnswers.some(acceptedAnswer =>
            normalizeAnswer(acceptedAnswer, question) === normalizedUserInput
          );
        } else {
          isCorrect = normalizeAnswer(userAnswer || '', question) === normalizeAnswer(question.answer || '', question);
        }
        break;
      case 'information-collection':
        // KAN-436: record-only text is resolved without judging correctness.
        points = 0;
        isCorrect = false;
        break;
      case 'true-false':
        isCorrect = userAnswer === question.answer;
        break;
      case 'paragraph':
        isCorrect = false;
        manualGradingPoints += points;
        points = 0;
        break;
      case 'multiple-choice-grid':
        let multipleChoiceGridData: any = { correctRows: 0, totalRows: 0, correctRowIndices: [] };

        if (Array.isArray(question.answer)) {
          // KAN-274: grade per-row against the accepted-column set, honoring
          // multi-accept rows and no-answer rows (correct iff learner left blank).
          // A blank no-answer row counts even when userAnswer is sparse/short.
          multipleChoiceGridData.totalRows = question.answer.length;
          for (let idx = 0; idx < question.answer.length; idx++) {
            const userCell = Array.isArray(userAnswer) ? userAnswer[idx] : undefined;
            if (isMcGridRowCorrectCell(userCell, question.answer[idx])) {
              multipleChoiceGridData.correctRows++;
              multipleChoiceGridData.correctRowIndices.push(idx);
            }
          }
          isCorrect = multipleChoiceGridData.correctRows === multipleChoiceGridData.totalRows;
        } else if (Array.isArray(userAnswer)) {
          multipleChoiceGridData.totalRows = userAnswer.length;
          multipleChoiceGridData.correctRows = 0;
          isCorrect = false;
        }

        (question as any)._gridData = multipleChoiceGridData;
        break;
      case 'checkbox-grid':
        let checkboxGridData: any = { correctRows: 0, totalRows: 0, correctRowIndices: [] };

        if (!question.answer && Array.isArray(userAnswer)) {
          checkboxGridData.totalRows = userAnswer.length;
          checkboxGridData.correctRows = 0;
          isCorrect = false;
          (question as any)._gridData = checkboxGridData;
          break;
        }

        if (Array.isArray(userAnswer) && Array.isArray(question.answer)) {
          checkboxGridData.totalRows = question.answer.length;

          userAnswer.forEach((rowAnswers: any, rowIdx: number) => {
            const correctRow = question.answer[rowIdx];

            if (!Array.isArray(rowAnswers) || !Array.isArray(correctRow)) {
              return;
            }

            const rowLengthMatch = rowAnswers.length === correctRow.length;
            const rowMatches = rowAnswers.every((checked: any, colIdx: number) => {
              const userVal = toBooleanValue(checked);
              const correctVal = toBooleanValue(correctRow[colIdx]);
              return userVal === correctVal;
            });

            if (rowLengthMatch && rowMatches) {
              checkboxGridData.correctRows++;
              checkboxGridData.correctRowIndices.push(rowIdx);
            }
          });

          isCorrect = checkboxGridData.correctRows === checkboxGridData.totalRows;
        }
        (question as any)._gridData = checkboxGridData;
        break;
      case 'matching': {
        // KAN-333: count correctly paired left items. answer[i] = correct
        // matchRight index for matchLeft[i]; userAnswer[i] = learner's pick.
        const matchingData: any = { correctPairs: 0, totalPairs: 0, correctPairIndices: [] };
        if (Array.isArray(question.answer)) {
          matchingData.totalPairs = question.answer.length;
          for (let idx = 0; idx < question.answer.length; idx++) {
            const picked = Array.isArray(userAnswer) ? userAnswer[idx] : undefined;
            if (picked !== null && picked !== undefined && Number(picked) === Number(question.answer[idx])) {
              matchingData.correctPairs++;
              matchingData.correctPairIndices.push(idx);
            }
          }
          isCorrect = matchingData.correctPairs === matchingData.totalPairs;
        }
        (question as any)._matchingData = matchingData;
        break;
      }
    }

    // Calculate points and partial credit
    let questionStatus: 'correct' | 'incorrect' | 'partial' | 'manual' | 'recorded' = 'incorrect';
    let questionScore = 0;
    let questionMaxScore = points;

    if (question.type === 'information-collection') {
      questionStatus = 'recorded';
      questionScore = 0;
      questionMaxScore = 0;
    } else if (question.type === 'paragraph') {
      questionStatus = 'manual';
      manualGradingPoints += points;
      points = 0;
    } else if (question.type === 'multiple-choice-grid' || question.type === 'checkbox-grid') {
      const gridData = (question as any)._gridData || { correctRows: 0, totalRows: 0, correctRowIndices: [] };
      questionMaxScore = points;

      // KAN-241: proportional scoring against the single question-level budget.
      // Each correct row earns an EQUAL share of the question's points — mirror
      // of the backend's `(correctWeight / totalWeight) * questionPoints` with
      // unweighted (weight=1) rows. This replaces the old sum-of-row-points,
      // which inflated a perfect floored grid to the row count.
      const correctIndices: number[] = Array.isArray(gridData.correctRowIndices) ? gridData.correctRowIndices : [];
      const totalRows: number = typeof gridData.totalRows === 'number' ? gridData.totalRows : 0;
      if (totalRows > 0 && correctIndices.length > 0) {
        questionScore = (correctIndices.length / totalRows) * questionMaxScore;
        if (questionScore > questionMaxScore) {
          questionScore = questionMaxScore;
        }
      } else {
        questionScore = 0;
      }

      if (questionScore === questionMaxScore && questionMaxScore > 0) {
        questionStatus = 'correct';
        isCorrect = true;
      } else if (questionScore > 0) {
        questionStatus = 'partial';
        isCorrect = false;
      } else {
        questionStatus = 'incorrect';
        isCorrect = false;
      }

      autoGradablePoints += questionMaxScore;
      earnedPoints += questionScore;
      autoEarnedPoints += questionScore;
    } else if (question.type === 'matching') {
      // KAN-333: mirror the backend gradeMatchingQuestion. 'partial' (default)
      // awards a proportional share; 'all-or-nothing' collapses to 0 unless
      // every pair is correct.
      const matchingData = (question as any)._matchingData || { correctPairs: 0, totalPairs: 0, correctPairIndices: [] };
      questionMaxScore = points;
      const totalPairs: number = typeof matchingData.totalPairs === 'number' ? matchingData.totalPairs : 0;
      const correctPairs: number = typeof matchingData.correctPairs === 'number' ? matchingData.correctPairs : 0;
      const scoringMode = (question.scoringMode || 'partial').toLowerCase().trim();

      if (totalPairs > 0) {
        if (scoringMode === 'all-or-nothing') {
          questionScore = correctPairs === totalPairs ? questionMaxScore : 0;
        } else {
          questionScore = correctPairs >= totalPairs
            ? questionMaxScore
            : (correctPairs / totalPairs) * questionMaxScore;
        }
      } else {
        questionScore = 0;
      }

      if (questionScore === questionMaxScore && questionMaxScore > 0) {
        questionStatus = 'correct';
        isCorrect = true;
      } else if (questionScore > 0) {
        questionStatus = 'partial';
        isCorrect = false;
      } else {
        questionStatus = 'incorrect';
        isCorrect = false;
      }

      autoGradablePoints += questionMaxScore;
      earnedPoints += questionScore;
      autoEarnedPoints += questionScore;
    } else if (question.type === 'select-all' && (question as any).scoringMode === 'partial') {
      const partialData = (question as any)._partialData || { score: 0, maxScore: points, correctCount: 0, incorrectCount: 0, totalCorrect: 0 };
      questionMaxScore = partialData.maxScore;
      questionScore = partialData.score;

      if (questionScore === questionMaxScore) {
        questionStatus = 'correct';
        isCorrect = true;
      } else if (questionScore > 0) {
        questionStatus = 'partial';
        isCorrect = false;
      } else {
        questionStatus = 'incorrect';
        isCorrect = false;
      }

      autoGradablePoints += questionMaxScore;
      earnedPoints += questionScore;
      autoEarnedPoints += questionScore;
    } else {
      questionStatus = isCorrect ? 'correct' : 'incorrect';
      questionScore = isCorrect ? points : 0;
      questionMaxScore = points;

      autoGradablePoints += points;
      if (isCorrect) {
        earnedPoints += points;
        autoEarnedPoints += points;
      }
    }

    const correctAnswerDisplay = resolveAnswerForDisplay(question, question.answer);
    const userAnswerDisplay = resolveAnswerForDisplay(question, userAnswer);

    feedback.push({
      questionIndex: index,
      isCorrect,
      status: questionStatus,
      score: questionScore,
      maxScore: questionMaxScore,
      correctAnswer: question.answer,
      userAnswer,
      correctAnswerDisplay,
      userAnswerDisplay,
      points,
      requiresManualGrading: question.type === 'paragraph',
      // KAN-254: carry all three explanation channels so instructor preview
      // renders the same explanation block a learner sees at the full tier.
      feedback: question.feedback
        ? { correct: question.feedback.correct, incorrect: question.feedback.incorrect, general: question.feedback.general }
        : null
    });
  });

  // Calculate percentage score.
  // KAN-239: compute the RAW percentage once, then derive two rounded values:
  //   - rawPercentageScore: HALF_UP to 1dp — the canonical pass/fail value
  //     (mirrors backend computeQuizResult). 79.5 stays 79.5 and fails 80%.
  //   - percentageScore: canonical 1-dp — DISPLAY only. Must never drive pass/fail.
  //     (KAN-525: was an integer, which disagreed with every other view.)
  const isPartiallyGraded = manualGradingPoints > 0;
  let rawPercentage: number;

  if (isPartiallyGraded) {
    rawPercentage = autoGradablePoints > 0 ? (autoEarnedPoints / autoGradablePoints) * 100 : 0;
  } else {
    rawPercentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
  }

  const rawPercentageScore = roundPercentage(rawPercentage);
  const percentageScore = roundPercentage(rawPercentage);

  return {
    feedback,
    autoEarnedPoints,
    autoGradablePoints,
    manualGradingPoints,
    totalPoints,
    earnedPoints,
    percentageScore,
    rawPercentageScore
  };
}

// ─── Grid feedback pure functions ─────────────────────────────────

export function canShowGridFeedback(
  question: QuizQuestion,
  questionResult: any | null,
  feedbackVisibility?: string
): boolean {
  // score_only and indicators levels don't show per-cell grid feedback
  if (feedbackVisibility === 'score_only' || feedbackVisibility === 'indicators') {
    return false;
  }
  if (questionResult) {
    return true;
  }
  if (question.answer) {
    return true;
  }
  return false;
}

export function isGridRowCorrect(
  question: QuizQuestion,
  userAnswer: any,
  questionResult: any | null,
  rowIndex: number
): boolean {
  if (!question || question.type !== 'multiple-choice-grid') {
    return false;
  }

  if (questionResult) {
    if (questionResult.correctRows && Array.isArray(questionResult.correctRows)) {
      return questionResult.correctRows.includes(rowIndex);
    }
  }

  // KAN-274: an answer source may carry a single column index, an array of
  // accepted columns, or [] (no-answer row, correct iff learner left blank).
  // The user array can be sparse for a left-blank no-answer row, so we must
  // tolerate a missing userAnswer/cell rather than bailing early.
  const answerSource = Array.isArray(question.answer)
    ? question.answer
    : (Array.isArray(questionResult?.correctAnswer) ? questionResult.correctAnswer : null);

  if (!answerSource) {
    return false;
  }

  const userCell = Array.isArray(userAnswer) ? userAnswer[rowIndex] : undefined;
  return isMcGridRowCorrectCell(userCell, answerSource[rowIndex]);
}

export function isGridCellCorrectAnswer(
  question: QuizQuestion,
  rowIndex: number,
  colIndex: number,
  questionResult?: any | null
): boolean {
  if (!question || question.type !== 'multiple-choice-grid') {
    return false;
  }

  // Server-scored learner mode: question.answer is stripped for security; use
  // the submission result instead. KAN-350: prefer correctAnswerRaw, which the
  // backend display-maps for shuffled rows, over the canonical (authored-order)
  // correctAnswer -- these per-cell markers index by DISPLAY rowIndex.
  const answerSource = Array.isArray(question.answer)
    ? question.answer
    : (Array.isArray(questionResult?.correctAnswerRaw)
        ? questionResult.correctAnswerRaw
        : (Array.isArray(questionResult?.correctAnswer) ? questionResult.correctAnswer : null));

  if (!answerSource) {
    return false;
  }

  // KAN-274: a cell may accept multiple columns. Reveal every accepted column
  // as a "correct answer" marker. A no-answer row ([]) has no correct column.
  return isMcGridColumnAccepted(answerSource[rowIndex], colIndex);
}

export function isCheckboxGridRowCorrect(
  question: QuizQuestion,
  userAnswer: any,
  questionResult: any | null,
  rowIndex: number
): boolean {
  if (!question || question.type !== 'checkbox-grid') {
    return false;
  }

  if (!userAnswer || !Array.isArray(userAnswer) || !userAnswer[rowIndex]) {
    return false;
  }

  if (questionResult) {
    if (questionResult.correctRows && Array.isArray(questionResult.correctRows)) {
      return questionResult.correctRows.includes(rowIndex);
    }
  }

  if (Array.isArray(question.answer)) {
    const correctRow = question.answer[rowIndex];
    const userRow = userAnswer[rowIndex];

    if (!Array.isArray(correctRow) || !Array.isArray(userRow)) {
      return false;
    }

    return correctRow.every((correctVal: any, colIdx: number) => {
      const userVal = toBooleanValue(userRow[colIdx]);
      const correctBool = toBooleanValue(correctVal);
      return userVal === correctBool;
    });
  }

  return false;
}

export function isCheckboxCellCorrectAnswer(
  question: QuizQuestion,
  rowIndex: number,
  colIndex: number,
  questionResult?: any | null
): boolean {
  if (!question || question.type !== 'checkbox-grid') {
    return false;
  }

  // Server-scored learner mode: question.answer is stripped; fall back to the
  // submission result. KAN-350: prefer display-mapped correctAnswerRaw over the
  // canonical correctAnswer -- these per-cell markers index by DISPLAY rowIndex.
  const answerSource = Array.isArray(question.answer)
    ? question.answer
    : (Array.isArray(questionResult?.correctAnswerRaw)
        ? questionResult.correctAnswerRaw
        : (Array.isArray(questionResult?.correctAnswer) ? questionResult.correctAnswer : null));

  if (!answerSource) {
    return false;
  }

  const correctRow = answerSource[rowIndex];
  if (!Array.isArray(correctRow)) {
    return false;
  }

  return toBooleanValue(correctRow[colIndex]);
}

export function isCheckboxCellUserSelected(
  userAnswer: any,
  rowIndex: number,
  colIndex: number
): boolean {
  if (!userAnswer || !Array.isArray(userAnswer) || !userAnswer[rowIndex]) {
    return false;
  }

  return toBooleanValue(userAnswer[rowIndex][colIndex]);
}

// ─── Submission pre-fill pure functions ──────────────────────────

/**
 * Normalizes a single submission answer value.
 * The backend may store short-answer/paragraph answers as objects like
 * `{ textAnswer: "canine", questionIndex: 2 }`. The UI textarea/input
 * binds via ngModel and calls `.toString()` on objects, producing
 * "[object Object]". This function extracts the text value when present.
 *
 * Non-object answers (numbers, booleans, strings, arrays, null) pass through unchanged.
 */
export function normalizeSubmissionAnswer(answer: any): any {
  if (
    answer !== null &&
    answer !== undefined &&
    typeof answer === 'object' &&
    !Array.isArray(answer) &&
    'textAnswer' in answer
  ) {
    return answer.textAnswer;
  }
  return answer;
}

/**
 * Maps submission answers to quiz questions by index, padding with null for missing answers
 * and truncating extras. Normalizes object answers (e.g., {textAnswer: "..."}) to plain strings.
 */
export function matchAnswersToQuestions(submissionAnswers: any[], questionCount: number): any[] {
  return Array.from({ length: questionCount }, (_, i) =>
    i < submissionAnswers.length ? normalizeSubmissionAnswer(submissionAnswers[i]) : null
  );
}

/**
 * Builds quiz feedback items from backend questionResults.
 * Pure function -- no side effects.
 * Handles cases where questionResults may exceed the questions array length.
 */
export function buildFeedbackFromBackendResults(
  questionResults: any[],
  questions: QuizQuestion[]
): QuizFeedbackItem[] {
  return questionResults.map((result: any, index: number) => {
    const question = index < questions.length ? questions[index] : undefined;
    // Backend resolves `correctAnswer` to display text and exposes the raw
    // index/boolean/array as `correctAnswerRaw` (KAN-178). Templates compare
    // raw values for === highlights ("✓ Correct" badge, border-warning); the
    // display text drives the human-readable feedback lines.
    const userAnswerDisplay = question
      ? resolveAnswerForDisplay(question, result.userAnswer)
      : result.userAnswer;
    const correctAnswer = result.correctAnswerRaw !== undefined
      ? result.correctAnswerRaw
      : result.correctAnswer;
    return {
      questionIndex: index,
      isCorrect: result.isCorrect ?? false,
      status: result.status ?? 'incorrect',
      score: result.score ?? result.points ?? 0,
      maxScore: result.maxScore ?? (question ? getQuestionPoints(question) : 0),
      correctAnswer,
      userAnswer: result.userAnswer,
      correctAnswerDisplay: result.correctAnswer,
      userAnswerDisplay,
      points: result.points ?? 0,
      requiresManualGrading: result.status === 'manual' || result.status === 'manual_review',
      feedback: result.feedback ?? null
    };
  });
}
